import find from 'lodash/find';
import { matchPath } from 'react-router-dom';
import { compile } from 'path-to-regexp';
import { stringify } from './urlHelpers';
// NOTE: This file imports urlHelpers.js, which may lead to circular dependency

const findRouteByName = (nameToFind, routes) => find(routes, route => route.name === nameToFind);

/**
 * E.g. ```const toListingPath = toPathByRouteName('ListingPage', routes);```
 * Then we can generate listing paths with given params (```toListingPath({ id: uuidX })```)
 */
const toPathByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Path "${nameToFind}" was not found.`);
  }
  return compile(route.path);
};

/**
 * Shorthand for single path call. (```pathByRouteName('ListingPage', routes, { id: uuidX });```)
 */
export const pathByRouteName = (nameToFind, routes, params = {}) => {
  const hasEmptySlug = params && params.hasOwnProperty('slug') && params.slug === '';
  const pathParams = hasEmptySlug ? { ...params, slug: 'no-slug' } : params;
  return toPathByRouteName(nameToFind, routes)(pathParams);
};

/**
 * Find the matching routes and their params for the given pathname
 *
 * @param {String} pathname - Full URL path from root with possible
 * search params and hash included
 *
 * @return {Array<{ route, params }>} - All matches as { route, params } objects if matches has
 * exact flag set to false. If not, an array containing just the first matched exact route is returned.
 */
export const matchPathname = (pathname, routeConfiguration) => {
  const matchedRoutes = routeConfiguration.reduce((matches, route) => {
    const { path, exact = true } = route;
    const match = matchPath(pathname, { path, exact });
    if (match) {
      matches.push({
        route,
        params: match.params || {},
      });
    }
    return matches;
  }, []);

  const matchedExactRoute = matchedRoutes.find(r => {
    return r.exact === true || r.exact == null;
  });

  // We return matched 'exact' path route only if such exists
  // and all matches if no exact flag exists.
  return matchedExactRoute ? [matchedExactRoute] : matchedRoutes;
};

/**
 * ResourceLocatorString is used to direct webapp to correct page.
 * In contrast to Universal Resource Locator (URL), this doesn't contain protocol, host, or port.
 */
export const createResourceLocatorString = (
  routeName,
  routes,
  pathParams = {},
  searchParams = {},
  hash = ''
) => {
  const searchQuery = stringify(searchParams);
  const includeSearchQuery = searchQuery.length > 0 ? `?${searchQuery}` : '';
  const path = pathByRouteName(routeName, routes, pathParams);
  return `${path}${includeSearchQuery}${hash}`;
};

/**
 * Find component related to route name
 * E.g. `const PageComponent = findComponentByRouteName('CheckoutPage', routes);`
 * Then we can call static methods of given component:
 * `dispatch(PageComponent.setInitialValues({ listing, bookingDates }));`
 *
 * @param {String} nameToFind - Route name
 * @param {Array<{ route }>} routes - Route configuration as flat array.
 *
 * @return {Route} - Route that matches the given route name.
 */
export const findRouteByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Component "${nameToFind}" was not found.`);
  }
  return route;
};

/**
 * Get the canonical URL from the given location
 *
 * @param {Array<{ route }>} routes - Route configuration as flat array
 * @param {Object} location - location object from React Router
 *
 * @return {String} Canonical URL of the given location
 *
 */
export const canonicalRoutePath = (routes, location, pathOnly = false) => {
  const { pathname, search, hash } = location;

  const matches = matchPathname(pathname, routes);
  const isListingRoute = matches.length === 1 && matches[0].route.name === 'ListingPage';

  if (isListingRoute) {
    // Remove the dynamic slug from the listing page canonical URL

    // Remove possible trailing slash
    const cleanedPathName = pathname.replace(/\/$/, '');
    const parts = cleanedPathName.split('/');

    if (parts.length !== 4) {
      throw new Error('Expected ListingPage route to have 4 parts');
    }
    const canonicalListingPathname = `/${parts[1]}/${parts[3]}`;
    return pathOnly ? canonicalListingPathname : `${canonicalListingPathname}${search}${hash}`;
  }

  return pathOnly ? pathname : `${pathname}${search}${hash}`;
};

/**
 * Format a URL path into a human-friendly, readable page name.
 * Handles dynamic route parameters by extracting and formatting slugs.
 *
 * @param {String} path - The path/URL to format (e.g. "/p/about" or "/l/web-dev/123")
 * @returns {String} The user-friendly page name
 */
export const formatPathName = (path) => {
  if (!path) return 'Unknown Page';


  console.log("formt paht name called", path)
  let pathname = path;
  try {
    if (path.startsWith('http://') || path.startsWith('https://')) {
      const urlObj = new URL(path);
      pathname = urlObj.pathname;
    } else {
      pathname = path.split('?')[0].split('#')[0];
    }
  } catch (e) {
    // Fall back to original path if URL parsing fails
  }

  // Normalize path by removing trailing slash if present (except for "/" itself)
  const cleanPath = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

  if (cleanPath === '/' || cleanPath === '') return 'Landing Page';
  if (cleanPath === '/p/about') return 'About Page';
  if (cleanPath === '/p/how-it-works') return 'How it Works Page';
  if (cleanPath.startsWith('/search')) return 'Search Page';
  if (cleanPath === '/s') return 'Talent Search Page';
  if (cleanPath === '/everything-fractional') return 'Everything Fractional Page (Blog List)';
  if (cleanPath === '/forteh-blog-templates') return 'Blog Template Selector Page';
  if (cleanPath === '/login') return 'Login Page';
  if (cleanPath === '/signup') return 'Signup Page';
  if (cleanPath === '/profile-settings') return 'Profile Settings Page';
  if (cleanPath === '/admin') return 'Admin Panel Page';
  if (cleanPath === '/track-user') return 'User Tracking Page';
  if (cleanPath.startsWith('/listings')) return 'My Listings Page';
  if (cleanPath === '/step1') return 'Edit Account - Step 1';
  if (cleanPath === '/step2') return 'Edit Account - Step 2';
  if (cleanPath === '/privacy-policy') return 'Privacy Policy Page';
  if (cleanPath === '/terms-of-service') return 'Terms of Service Page';
  if (cleanPath === '/newsletter') return 'Newsletter Page';
  if (cleanPath === '/linkedin') return 'LinkedIn Page';

  // Dynamic routes
  if (cleanPath.startsWith('/everything-fractional/')) {
    const parts = cleanPath.split('/');
    const slug = parts[3] ? parts[3].replace(/-/g, ' ') : '';
    return `Blog Post: ${slug || 'Details'}`;
  }
  if (cleanPath.startsWith('/forteh-blog')) {
    return 'Forteh Blog';
  }
  if (cleanPath.startsWith('/l/')) {
    const parts = cleanPath.split('/');
    // Check if it is the edit/new listing wizard path: /l/:slug/:id/:type/:tab
    if (parts.length >= 6) {
      const type = parts[4];
      const tab = parts[5];
      
      const isRoleWizard = ['requirements', 'how', 'what', 'where'].includes(tab);
      const action = (type === 'new' || type === 'draft')
        ? (isRoleWizard ? 'Create Role' : 'Create Profile')
        : (isRoleWizard ? 'Edit Role' : 'Edit Profile');
      
      let tabName = '';
      if (tab === 'requirements') {
        tabName = 'What You Need';
      } else if (tab === 'how') {
        tabName = "How They'll Do It";
      } else if (tab === 'what') {
        tabName = "What You'll Pay";
      } else if (tab === 'where') {
        tabName = "Where They'll Work";
      } else if (tab === 'details') {
        tabName = 'The Job';
      } else if (tab === 'skills') {
        tabName = 'How I Do';
      } else if (tab === 'pricing') {
        tabName = 'What I Earn';
      } else if (tab === 'extra-detail') {
        tabName = "What I've Learned";
      } else if (tab === 'what-say') {
        tabName = "What I've Done";
      } else if (tab === 'verfiy-yourself') {
        tabName = 'Verify Yourself';
      } else if (tab === 'photos') {
        tabName = 'Selfies';
      } else {
        tabName = tab ? tab.charAt(0).toUpperCase() + tab.slice(1).replace(/-/g, ' ') : '';
      }
      
      return `${action}: ${tabName}`;
    }
    if (parts[4] === 'checkout') {
      return 'Checkout Page';
    }
    const slug = parts[2] ? parts[2].replace(/-/g, ' ') : '';
    return `Profile: ${slug || 'Detail'}`;
  }
  if (cleanPath.startsWith('/u/')) {
    return 'User Profile Page';
  }
  if (cleanPath === '/inbox/orders') return 'Inbox: Connections';
  if (cleanPath === '/inbox/sales') return 'Inbox: Jobs';
  if (cleanPath === '/inbox/saved-notes') return 'Inbox: Saved Profiles';
  if (cleanPath === '/inbox/later') return 'Inbox: Review Later';
  if (cleanPath.startsWith('/inbox')) {
    return 'Inbox Page';
  }
  if (cleanPath.startsWith('/account/')) {
    const sub = cleanPath.replace('/account/', '').replace(/-/g, ' ');
    return `Account: ${sub.charAt(0).toUpperCase() + sub.slice(1)}`;
  }

  if (cleanPath.startsWith('/order/')) {
    return 'Order Page';
  }
  if (cleanPath.startsWith('/sale/')) {
    return 'Sale Page';
  }

  if (cleanPath.startsWith('/matched-listings/')) {
    return 'Matched Listings Page';
  }

  return cleanPath;
};

